import { Layout } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { useLocation } from 'react-router-dom';
import { SubHeader } from 'src/components/v2/Header';
import { Sidebar } from 'src/components/v2/Sidebar';

import { useContext, useEffect } from 'react';
import urlPath from 'src/constants/urlPath';
import { AppContext } from 'src/contexts/app.context';
import { TicketContext, TicketProvider } from 'src/contexts/ticket.context';
import { HrTicketDetailLayout, OfficeTicketDetailLayout } from 'src/pages/v2/HrTicket/components/Layout';
import TicketDetailLayout from 'src/pages/v2/Ticket/TicketDetail';
import styles from './TicketLayout.module.scss';

interface Props {
  children?: React.ReactNode;
  hasSidebar?: boolean;
  hasSubHeader?: boolean;
  isHideDetailLayout?: boolean;
}

export default function TicketLayout({
  children,
  hasSidebar = true,
  hasSubHeader = true,
  isHideDetailLayout = false
}: Props) {
  const location = useLocation();
  const { ticketStatusId, approvalStatus } = useContext(TicketContext);
  const { ticketSelected, setTicketSelected, setIsTicketDetailCollapsed, setIsSidebarCollapsed } =
    useContext(AppContext);

  // Reset ticket selection when status changes
  useEffect(() => {
    if (ticketStatusId || approvalStatus) {
      setTicketSelected(undefined);
    }
  }, [ticketStatusId, approvalStatus, setTicketSelected]);

  // Handle ticket detail panel and sidebar state
  useEffect(() => {
    if (ticketSelected !== undefined) {
      setIsTicketDetailCollapsed(false);
      setIsSidebarCollapsed(true);
    } else {
      setIsTicketDetailCollapsed(true);
      setIsSidebarCollapsed(false);
    }
  }, [ticketSelected, setIsTicketDetailCollapsed, setIsSidebarCollapsed]);

  // Determine which detail layout to render based on current path
  const renderDetailLayout = () => {
    if (isHideDetailLayout) return null;

    if (location.pathname.includes(urlPath.ticket.location)) {
      return <TicketDetailLayout />;
    }

    if (
      location.pathname.includes(urlPath.hrTicket.location) ||
      location.pathname.includes(urlPath.hrConfirm.location)
    ) {
      return <HrTicketDetailLayout />;
    }

    if (location.pathname.includes(urlPath.corporateOperation.location)) {
      return <OfficeTicketDetailLayout />;
    }

    return null;
  };

  return (
    <div className={styles['ticket-layout']}>
      <TicketProvider>
        <SubHeader hasSubHeader={hasSubHeader} />
        <Layout hasSider>
          {hasSidebar && <Sidebar />}
          <Layout hasSider className={!hasSidebar ? styles['ticket-container'] : undefined}>
            <Content>
              <div className={styles['ticket-content-wrapper']}>{children}</div>
            </Content>
            {renderDetailLayout()}
          </Layout>
        </Layout>
      </TicketProvider>
    </div>
  );
}
